// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

// Time & Cost Audit (F5, Admin). The API mounts /v1/time-cost-audit on F5
// only and gates it on Admin, like the route and the nav item.

const messageOf = (error, fallback) => {
  const status = error.response && error.response.status
  const serverMessage = error.response && error.response.data && error.response.data.message
  if (serverMessage) return serverMessage
  if (status === 403) return 'You do not have access to Teams presence.'
  return error.message || fallback
}

// "Teams presence time" for one month ('YYYY-MM'; omitted = the API's
// default, the previous calendar month): per-employee Teams presence (an
// estimate, not worked time) and usage counts.
export const getTimeCostAuditTeams = createAsyncThunk('timeCostAudit/teams', async ({ month } = {}, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/time-cost-audit/teams-activity', {
      params: month ? { month } : {},
      skipGlobalErrorHandler: true
    })
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load Teams presence time.'))
  }
})

// Kept even when the answer arrives after the reader stepped on: it is still
// that month's data.
const remember = (state, section, payload) => {
  if (payload && payload.month) state.seen[section][payload.month] = payload
}

// The day view for one Kyiv day ('YYYY-MM-DD'): each person's live
// timeline (active blocks, calls) and totals.
export const getTimeCostAuditTeamsDay = createAsyncThunk('timeCostAudit/teamsDay', async ({ day }, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/time-cost-audit/teams-activity/day', {
      params: { day },
      skipGlobalErrorHandler: true
    })
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load the Teams day.'))
  }
})

/** The key an employee view is remembered under: 'YYYY-MM:<userId>'. */
export const employeeKey = (month, userId) => `${month}:${userId}`

// The employee view: one person's month (their month row, each day with its
// live timeline); person null when they have no row that month.
export const getTimeCostAuditTeamsEmployee = createAsyncThunk('timeCostAudit/teamsEmployee', async ({ month, userId }, { rejectWithValue }) => {
  try {
    const response = await axios.get('/v1/time-cost-audit/teams-activity/employee', {
      params: month ? { userId, month } : { userId },
      skipGlobalErrorHandler: true
    })
    return response.data
  } catch (error) {
    return rejectWithValue(messageOf(error, 'Could not load this person’s Teams presence.'))
  }
})

export const timeCostAuditSlice = createSlice({
  name: 'timeCostAudit',
  initialState: {
    teams: null,
    teamsLoading: false,
    teamsError: null,
    // The request whose answer the page shows: a slower answer for a month
    // the reader already stepped past is dropped.
    teamsRequestId: null,
    // Every payload this visit has loaded, by month, so stepping back to a
    // month shows it at once while it refreshes.
    seen: { teams: {} },
    // The day view, keyed by day.
    teamsDay: null,
    teamsDayError: null,
    teamsDayRequestId: null,
    seenDays: {},
    // The employee view, keyed by employeeKey(month, userId).
    teamsEmployee: null,
    teamsEmployeeKey: null,
    teamsEmployeeError: null,
    teamsEmployeeRequestId: null,
    seenEmployees: {}
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTimeCostAuditTeams.pending, (state, action) => {
        state.teamsLoading = true
        state.teamsError = null
        state.teamsRequestId = action.meta.requestId
      })
      .addCase(getTimeCostAuditTeams.fulfilled, (state, action) => {
        remember(state, 'teams', action.payload)
        if (action.meta.requestId !== state.teamsRequestId) return
        state.teamsLoading = false
        state.teams = action.payload
      })
      .addCase(getTimeCostAuditTeamsDay.pending, (state, action) => {
        state.teamsDayError = null
        state.teamsDayRequestId = action.meta.requestId
      })
      .addCase(getTimeCostAuditTeamsDay.fulfilled, (state, action) => {
        if (action.payload && action.payload.day) state.seenDays[action.payload.day] = action.payload
        if (action.meta.requestId !== state.teamsDayRequestId) return
        state.teamsDay = action.payload
      })
      .addCase(getTimeCostAuditTeamsDay.rejected, (state, action) => {
        if (action.meta.requestId !== state.teamsDayRequestId) return
        state.teamsDay = null
        state.teamsDayError = action.payload || 'Could not load the Teams day.'
      })
      .addCase(getTimeCostAuditTeamsEmployee.pending, (state, action) => {
        state.teamsEmployeeError = null
        state.teamsEmployeeRequestId = action.meta.requestId
      })
      .addCase(getTimeCostAuditTeamsEmployee.fulfilled, (state, action) => {
        const key = action.payload && action.payload.month ? employeeKey(action.payload.month, action.meta.arg.userId) : null
        if (key) state.seenEmployees[key] = action.payload
        if (action.meta.requestId !== state.teamsEmployeeRequestId) return
        state.teamsEmployee = action.payload
        state.teamsEmployeeKey = key
      })
      .addCase(getTimeCostAuditTeamsEmployee.rejected, (state, action) => {
        if (action.meta.requestId !== state.teamsEmployeeRequestId) return
        state.teamsEmployee = null
        state.teamsEmployeeKey = null
        state.teamsEmployeeError = action.payload || 'Could not load this person’s Teams presence.'
      })
      .addCase(getTimeCostAuditTeams.rejected, (state, action) => {
        if (action.meta.requestId !== state.teamsRequestId) return
        state.teamsLoading = false
        state.teams = null
        state.teamsError = action.payload || 'Could not load Teams presence time.'
      })
  }
})

export default timeCostAuditSlice.reducer
